'use client';

import { Button } from '@ui/components/button';
import { AppCard, PageLayout } from '@ui/components/layouts';
import { PageHeaderWithGardenSelector } from '@/src/components/PageHeaderWithGardenSelector';
import { PageContentLoading } from '@/src/components/PageContentLoading';
import { text } from '@modules/sowing-calendar/text';
import { textBusinessLogic } from '@modules/sowing-calendar/text_business_logic';
import {
  canMarkSown,
  formatMonthDayLabel,
  formatShortDateLabel,
  monthCellVisibleEntries,
  tagVariantForEntry,
  type AgendaSection,
  type CalendarDayCell,
  type SowingCalendarEntry,
  type SowingCalendarViewMode,
  type TagVariant,
  type WeekDayColumn,
} from '@modules/sowing-calendar/business_logic';
import type { SowingCalendarProps } from '@modules/sowing-calendar/containers/sowing_calendar_container';
import type { Id } from 'convex/_generated/dataModel';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCheck } from '@micks-yard/icons/solid/faCheck';
import { faChevronLeft } from '@micks-yard/icons/solid/faChevronLeft';
import { faChevronRight } from '@micks-yard/icons/solid/faChevronRight';
import { faClock } from '@micks-yard/icons/solid/faClock';
import { faLeaf } from '@micks-yard/icons/solid/faLeaf';
import { faSeedling } from '@micks-yard/icons/solid/faSeedling';
import { faSun } from '@micks-yard/icons/solid/faSun';
import { useSowingCalendarStore } from '@shared/store/useSowingCalendarStore';
import { text as sharedText } from '@shared/text';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useEffect, type CSSProperties } from 'react';

const VIEW_MODES: SowingCalendarViewMode[] = ['month', 'week', 'agenda'];
const ACTION_SLOT_WIDTH = 88;

const monthWeekHeaderStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: 6,
  paddingInline: 2,
};

const monthWeekRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: 6,
};

const monthCellBaseStyle: CSSProperties = {
  minWidth: 0,
  maxWidth: '100%',
  height: 112,
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  overflow: 'hidden',
  borderRadius: 4,
  boxSizing: 'border-box',
};

const monthCellTagStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  fontSize: 10,
  lineHeight: 1.3,
  padding: '2px 6px',
  borderRadius: 4,
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

function viewModeLabel(mode: SowingCalendarViewMode): string {
  switch (mode) {
    case 'month':
      return text.viewMonth;
    case 'week':
      return text.viewWeek;
    case 'agenda':
      return text.viewAgenda;
  }
}

function StatusTag({ entry, compact = false }: { entry: SowingCalendarEntry; compact?: boolean }) {
  const label = textBusinessLogic.statusTag(entry.status, entry.sowMethod);
  if (!label) return null;

  const variant: TagVariant = tagVariantForEntry(entry);

  const style: CSSProperties =
    variant === 'accent'
      ? {
          backgroundColor: 'var(--green3, #dcfce7)',
          color: 'var(--green11, #166534)',
          borderColor: 'transparent',
        }
      : variant === 'neutral'
        ? {
            backgroundColor: 'var(--gray4, #e5e7eb)',
            color: 'var(--gray11, #374151)',
            borderColor: 'transparent',
          }
        : {
            backgroundColor: 'transparent',
            color: 'var(--green10, #16a34a)',
            borderColor: 'var(--green7, #86efac)',
          };

  return (
    <span
      className={`inline-block shrink-0 rounded-md text-center tracking-wide ${
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-[3px] text-[11px]'
      } ${variant === 'outline' ? 'border' : ''} ${compact ? '' : 'min-w-[110px]'}`}
      style={style}>
      {label}
    </span>
  );
}

function SeedCountBlock({ entry }: { entry: SowingCalendarEntry }) {
  const muted = entry.status === 'out-of-season';
  if (entry.status === 'sown') {
    return (
      <div className="flex w-[150px] shrink-0 flex-col items-end">
        <span
          className={`text-[13px] font-medium ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>
          {text.sownSlots(entry.slots)}
        </span>
        <span className="text-muted-foreground text-[11px]">{text.slotsFilled}</span>
      </div>
    );
  }

  return (
    <div className="flex w-[150px] shrink-0 flex-col items-end">
      <span
        className={`text-[13px] font-medium ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>
        {text.seedsStrong(entry.seeds)}
      </span>
      <span className="text-muted-foreground text-[11px]">
        {text.slotsBuffer(entry.slots, entry.buffer)}
      </span>
    </div>
  );
}

function ActionSlot({
  entry,
  onMarkSown,
}: {
  entry: SowingCalendarEntry;
  onMarkSown: (plantId: string, currentHasSown: boolean) => void;
}) {
  if (canMarkSown(entry.status)) {
    return (
      <Button
        type="button"
        variant="ghost"
        className="h-auto shrink-0 rounded-md px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-100"
        onClick={() => onMarkSown(entry.plantId, entry.hasSown)}>
        {text.markSown}
      </Button>
    );
  }

  return <div className="shrink-0" style={{ width: ACTION_SLOT_WIDTH }} />;
}

function ViewModeSwitcher({
  viewMode,
  onViewModeChange,
}: {
  viewMode: SowingCalendarViewMode;
  onViewModeChange: (mode: SowingCalendarViewMode) => void;
}) {
  return (
    <div className="border-border bg-background flex overflow-hidden rounded-lg border">
      {VIEW_MODES.map((mode) => {
        const active = mode === viewMode;
        return (
          <Button
            key={mode}
            type="button"
            variant="ghost"
            className={`h-auto rounded-none px-3.5 py-2 text-[13px] font-medium ${
              active
                ? 'text-green-600 shadow-[inset_0_0_0_1px_var(--green7,#86efac)]'
                : 'text-foreground'
            }`}
            style={active ? { backgroundColor: 'var(--green2, #f0fdf4)' } : undefined}
            onClick={() => onViewModeChange(mode)}>
            {viewModeLabel(mode)}
          </Button>
        );
      })}
    </div>
  );
}

function PeriodNav({
  label,
  onPrev,
  onNext,
}: {
  label: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center gap-3.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="rounded-full text-green-600 hover:bg-green-100"
        aria-label={text.prevPeriod}
        onClick={onPrev}>
        <FaIcon icon={faChevronLeft} className="size-3.5" />
      </Button>
      <span className="text-xl font-medium tracking-[-0.3px]">{label}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="rounded-full text-green-600 hover:bg-green-100"
        aria-label={text.nextPeriod}
        onClick={onNext}>
        <FaIcon icon={faChevronRight} className="size-3.5" />
      </Button>
    </div>
  );
}

function OutlineTag({ children, muted = false }: { children: string; muted?: boolean }) {
  return (
    <span
      className={`rounded-md border border-green-300 px-2.5 py-[3px] text-[11px] tracking-wide text-green-600 ${
        muted ? 'opacity-70' : ''
      }`}>
      {children}
    </span>
  );
}

function AgendaRow({
  entry,
  onMarkSown,
}: {
  entry: SowingCalendarEntry;
  onMarkSown: SowingCalendarProps['onMarkSown'];
}) {
  const borderColor =
    entry.status === 'due-now'
      ? 'var(--green8, #22c55e)'
      : entry.status === 'out-of-season'
        ? 'var(--gray6, #d1d5db)'
        : 'var(--gray7, #9ca3af)';
  const opacity =
    entry.status === 'out-of-season'
      ? 0.55
      : entry.status === 'sown'
        ? 0.7
        : entry.status === 'upcoming'
          ? 0.85
          : 1;

  return (
    <div
      className="flex flex-wrap items-center gap-4 border-l-2 py-3 pr-1.5 pl-4"
      style={{ borderLeftColor: borderColor, opacity }}>
      <span className="text-muted-foreground w-[120px] shrink-0 text-xs">
        {textBusinessLogic.timeBucketLabel({
          status: entry.status,
          pinDate: entry.pinDate,
          windowLabel: entry.windowLabel,
          formatPinDate: formatMonthDayLabel,
        })}
      </span>
      <div className="flex min-w-[180px] flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-medium tracking-[-0.2px]">{entry.plantName}</span>
        <span className="text-muted-foreground text-xs">
          {textBusinessLogic.entrySubtext({
            status: entry.status,
            sowMethod: entry.sowMethod,
            windowLabel: entry.windowLabel,
          })}
        </span>
      </div>
      <SeedCountBlock entry={entry} />
      <StatusTag entry={entry} />
      <ActionSlot entry={entry} onMarkSown={onMarkSown} />
    </div>
  );
}

function AgendaView({
  sections,
  weekRangeShort,
  onMarkSown,
}: {
  sections: AgendaSection[];
  weekRangeShort: string;
  onMarkSown: SowingCalendarProps['onMarkSown'];
}) {
  return (
    <div className="flex flex-col gap-4">
      {sections.map((section) => {
        if (section.entries.length === 0) return null;
        const accent = textBusinessLogic.isSectionAccent(section.id);
        const detail =
          section.id === 'due-now'
            ? weekRangeShort.replace(/, \d{4}$/, '')
            : section.id === 'upcoming' && section.entries[0]?.windowLabel
              ? section.entries[0].windowLabel.split(' – ')[0]
              : undefined;

        return (
          <div key={section.id} className="flex flex-col gap-2">
            <span
              className={`mb-0.5 text-[11px] font-normal tracking-[1.6px] uppercase ${
                accent ? 'text-green-600' : 'text-muted-foreground'
              }`}>
              {textBusinessLogic.sectionTitle(section.id, detail)}
            </span>
            <div className="flex flex-col">
              {section.entries.map((entry) => (
                <AgendaRow key={entry.plantId} entry={entry} onMarkSown={onMarkSown} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LegendItem({
  icon,
  label,
  muted = false,
}: {
  icon: IconDefinition;
  label: string;
  muted?: boolean;
}) {
  return (
    <div className={`flex items-center gap-1.5 ${muted ? 'opacity-60' : ''}`}>
      <FaIcon icon={icon} className="size-3.5 text-green-600 opacity-80" />
      <span className="text-muted-foreground text-xs">{label}</span>
    </div>
  );
}

function MonthLegend() {
  return (
    <div className="flex flex-wrap items-center gap-[18px]">
      <LegendItem icon={faSun} label={text.legendOutdoor} />
      <LegendItem icon={faSeedling} label={text.legendIndoor} />
      <LegendItem icon={faCheck} label={text.legendSown} muted />
      <LegendItem icon={faClock} label={text.legendOutOfSeason} muted />
    </div>
  );
}

function MonthCellTag({ entry }: { entry: SowingCalendarEntry }) {
  const variant = tagVariantForEntry(entry);
  const label = entry.status === 'sown' ? text.monthTagSown(entry.plantName) : entry.plantName;

  const style: CSSProperties = {
    ...monthCellTagStyle,
    border: variant === 'outline' ? '1px solid var(--green7, #86efac)' : '1px solid transparent',
    background:
      variant === 'accent'
        ? 'var(--green3, #dcfce7)'
        : variant === 'neutral'
          ? 'var(--gray4, #e5e7eb)'
          : 'transparent',
    color: variant === 'neutral' ? 'var(--gray11, #4b5563)' : 'var(--green11, #166534)',
    textDecoration: entry.status === 'sown' ? 'line-through' : undefined,
    opacity: entry.status === 'sown' ? 0.85 : 1,
  };

  return <span style={style}>{label}</span>;
}

function MonthDayDetail({
  entries,
  dateLabel,
  onMarkSown,
}: {
  entries: SowingCalendarEntry[];
  dateLabel: string;
  onMarkSown: SowingCalendarProps['onMarkSown'];
}) {
  return (
    <div className="border-border flex flex-col gap-2.5 border-t pt-3">
      <span className="text-muted-foreground text-[11px] tracking-[1.2px] uppercase">
        {text.selectedDayTitle(dateLabel)}
      </span>

      {entries.length === 0 ? (
        <p className="text-muted-foreground text-sm">{text.emptySelectedDay}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {entries.map((entry) => (
            <div
              key={entry.plantId}
              className={`border-border bg-background flex flex-col gap-1.5 rounded-lg border px-3.5 py-3 ${
                entry.status === 'sown' ? 'opacity-80' : ''
              }`}>
              <div className="flex items-start justify-between gap-2">
                <span
                  className={`text-sm font-medium tracking-[-0.2px] ${
                    entry.status === 'sown' ? 'line-through' : ''
                  }`}>
                  {entry.status === 'sown' ? text.monthTagSown(entry.plantName) : entry.plantName}
                </span>
                <StatusTag entry={entry} compact />
              </div>
              <span className="text-muted-foreground text-xs opacity-90">
                {textBusinessLogic.entrySubtext({
                  status: entry.status,
                  sowMethod: entry.sowMethod,
                  windowLabel: entry.windowLabel,
                })}
              </span>
              <span className="text-muted-foreground text-xs">
                <span className="text-foreground text-xs font-medium">
                  {text.seedsStrong(entry.seeds)}
                </span>
                {` (${text.slotsBuffer(entry.slots, entry.buffer)})`}
              </span>
              <ActionSlot entry={entry} onMarkSown={onMarkSown} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MonthView({
  cells,
  footerEntries,
  onMarkSown,
}: {
  cells: CalendarDayCell[];
  footerEntries: SowingCalendarEntry[];
  onMarkSown: SowingCalendarProps['onMarkSown'];
}) {
  const selectedMonthDay = useSowingCalendarStore((s) => s.selectedMonthDay);
  const setSelectedMonthDay = useSowingCalendarStore((s) => s.setSelectedMonthDay);

  useEffect(() => {
    if (selectedMonthDay && cells.some((c) => c.isoDate === selectedMonthDay)) return;
    const todayCell = cells.find((c) => c.isToday && c.inCurrentMonth);
    const firstInMonth = cells.find((c) => c.inCurrentMonth);
    setSelectedMonthDay(todayCell?.isoDate ?? firstInMonth?.isoDate ?? null);
  }, [cells, selectedMonthDay, setSelectedMonthDay]);

  const selectedCell = cells.find((c) => c.isoDate === selectedMonthDay) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <MonthLegend />

      <div className="flex w-full flex-col gap-1.5">
        <div style={monthWeekHeaderStyle}>
          {sharedText.daysOfWeekMondayFirst.map((day) => (
            <span
              key={day}
              className="text-muted-foreground text-[11px] tracking-[0.8px] uppercase">
              {day.slice(0, 3)}
            </span>
          ))}
        </div>

        <div className="flex w-full flex-col gap-1.5">
          {Array.from({ length: cells.length / 7 }, (_, weekIndex) => (
            <div key={weekIndex} style={monthWeekRowStyle}>
              {cells.slice(weekIndex * 7, weekIndex * 7 + 7).map((cell) => {
                const selected = cell.isoDate === selectedMonthDay;
                const { visible, overflowCount } = monthCellVisibleEntries(cell.entries);
                const cellStyle: CSSProperties = {
                  ...monthCellBaseStyle,
                  cursor: 'pointer',
                  border: selected
                    ? '1.5px solid var(--green9, #16a34a)'
                    : cell.isToday
                      ? '1.5px solid var(--green8, #22c55e)'
                      : '1px solid var(--borderColor, #e5e7eb)',
                  background: selected
                    ? 'var(--green3, #dcfce7)'
                    : cell.isToday
                      ? 'var(--green2, #f0fdf4)'
                      : 'var(--background, #fff)',
                  opacity: cell.inCurrentMonth ? 1 : 0.4,
                };

                return (
                  <div
                    key={cell.isoDate}
                    style={cellStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedMonthDay(cell.isoDate)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedMonthDay(cell.isoDate);
                      }
                    }}>
                    <span
                      className={`shrink-0 text-xs ${
                        cell.isToday || selected
                          ? 'font-medium text-green-600'
                          : 'text-foreground font-normal'
                      }`}>
                      {cell.isToday ? `${cell.dayOfMonth} · Today` : cell.dayOfMonth}
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minHeight: 0,
                        flex: 1,
                        overflow: 'hidden',
                      }}>
                      {visible.map((entry) => (
                        <MonthCellTag key={entry.plantId} entry={entry} />
                      ))}
                      {overflowCount > 0 && (
                        <span className="text-muted-foreground shrink-0 text-[10px]">
                          {text.morePlants(overflowCount)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedCell && (
        <MonthDayDetail
          entries={selectedCell.entries}
          dateLabel={formatShortDateLabel(selectedCell.isoDate)}
          onMarkSown={onMarkSown}
        />
      )}

      {footerEntries.length > 0 && (
        <div className="border-border flex flex-wrap items-center gap-2 border-t pt-2">
          <span className="text-muted-foreground text-[11px] tracking-[1.2px] uppercase">
            {text.notInSeasonFooter}
          </span>
          {footerEntries.map((entry) => (
            <OutlineTag key={entry.plantId} muted>
              {text.footerPlantOpens(entry.plantName, entry.windowLabel)}
            </OutlineTag>
          ))}
        </div>
      )}
    </div>
  );
}

function WeekView({
  columns,
  footerEntries,
  weekActionCount,
  weekSeedCount,
  onMarkSown,
}: {
  columns: WeekDayColumn[];
  footerEntries: SowingCalendarEntry[];
  weekActionCount: number;
  weekSeedCount: number;
  onMarkSown: SowingCalendarProps['onMarkSown'];
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <span className="rounded-md bg-green-100 px-2.5 py-[3px] text-[11px] font-medium tracking-wide text-green-800">
          {text.weekSummary(weekActionCount, weekSeedCount)}
        </span>
      </div>

      <div className="flex items-stretch gap-2 overflow-auto">
        {columns.map((column) => (
          <div
            key={column.isoDate}
            className={`flex min-w-[118px] flex-1 flex-col gap-2 rounded-lg p-2.5 ${
              column.isToday ? 'border border-green-300 bg-green-50' : 'border border-transparent'
            }`}>
            <div className="flex flex-col gap-0.5">
              <span className="text-muted-foreground text-[11px] tracking-[0.6px] uppercase">
                {column.dayName.slice(0, 3)}
              </span>
              <span
                className={`text-[15px] font-medium ${column.isToday ? 'text-green-600' : 'text-foreground'}`}>
                {column.dayOfMonth}
              </span>
            </div>
            {column.entries.map((entry) => (
              <div
                key={entry.plantId}
                className={`border-border bg-background flex flex-col gap-1.5 rounded-lg border p-2.5 ${
                  entry.status === 'sown' ? 'opacity-75' : ''
                }`}>
                <div className="flex items-center justify-between gap-1">
                  <span
                    className={`text-[13px] font-medium tracking-[-0.2px] ${
                      entry.status === 'sown' ? 'line-through' : ''
                    }`}>
                    {entry.status === 'sown' ? text.monthTagSown(entry.plantName) : entry.plantName}
                  </span>
                </div>
                <StatusTag entry={entry} compact />
                <span className="text-muted-foreground text-[11px]">
                  {text.seedsStrong(entry.seeds)}{' '}
                  <span className="text-muted-foreground text-[11px]">
                    ({entry.slots}+{entry.buffer})
                  </span>
                </span>
                <ActionSlot entry={entry} onMarkSown={onMarkSown} />
              </div>
            ))}
          </div>
        ))}
      </div>

      {footerEntries.length > 0 && (
        <div className="border-border flex flex-wrap items-center gap-2 border-t pt-2">
          <span className="text-muted-foreground text-[11px] tracking-[1.2px] uppercase">
            {text.laterFooter}
          </span>
          {footerEntries.map((entry) => (
            <OutlineTag key={entry.plantId} muted>
              {text.footerPlantOpens(entry.plantName, entry.windowLabel)}
            </OutlineTag>
          ))}
        </div>
      )}
    </div>
  );
}

export function SowingCalendarView(props: SowingCalendarProps) {
  if (props.isLoading) {
    return <PageContentLoading />;
  }

  const showPeriodNav = props.viewMode === 'month' || props.viewMode === 'week';

  return (
    <PageLayout maxWidth={1280}>
      <PageHeaderWithGardenSelector
        title={text.title}
        description={text.description}
        gardens={props.gardens}
        selectedGardenId={props.selectedGardenId}
        onGardenChange={(id) => props.onGardenChange(id as Id<'gardens'> | null)}
        gardenSelectLabel={text.gardenSelectLabel}
      />

      <AppCard className="border-border bg-background mt-4 rounded-[14px] border p-7">
        <div className="flex flex-col gap-[18px]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {showPeriodNav ? (
              <PeriodNav
                label={props.focusedDateLabel}
                onPrev={props.onPrevPeriod}
                onNext={props.onNextPeriod}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xl font-medium tracking-[-0.3px]">{text.agendaTitle}</span>
                <OutlineTag>{props.todayDateLabel}</OutlineTag>
              </div>
            )}
            <ViewModeSwitcher viewMode={props.viewMode} onViewModeChange={props.onViewModeChange} />
          </div>

          {!props.selectedGarden ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <FaIcon icon={faLeaf} className="text-foreground size-7 opacity-30" />
              <p className="text-muted-foreground text-sm">{text.selectGardenPrompt}</p>
            </div>
          ) : props.agendaSections.every((s) => s.entries.length === 0) ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <FaIcon icon={faLeaf} className="text-foreground size-7 opacity-30" />
              <p className="text-muted-foreground text-sm">{text.noPlantsInGarden}</p>
              <p className="text-muted-foreground text-xs">{text.addPlantsFirst}</p>
            </div>
          ) : (
            <>
              {props.viewMode === 'agenda' && (
                <AgendaView
                  sections={props.agendaSections}
                  weekRangeShort={props.weekRangeShort}
                  onMarkSown={props.onMarkSown}
                />
              )}
              {props.viewMode === 'month' && (
                <MonthView
                  cells={props.monthCells}
                  footerEntries={props.monthFooterEntries}
                  onMarkSown={props.onMarkSown}
                />
              )}
              {props.viewMode === 'week' && (
                <WeekView
                  columns={props.weekColumns}
                  footerEntries={props.weekFooterEntries}
                  weekActionCount={props.weekActionCount}
                  weekSeedCount={props.weekSeedCount}
                  onMarkSown={props.onMarkSown}
                />
              )}
            </>
          )}
        </div>
      </AppCard>
    </PageLayout>
  );
}
